'use client';
import React from 'react';

import { IRepositoryCompareEntry } from '@/lib/repositoryCompareApi';
import { Row } from '@/ui/primatives';
import Tag from '@/ui/Tag';
import * as Icon from '@/ui/Icon';

import * as format from '@/lib/format';

const AddTag: React.FC<{ children: React.ReactNode | string }> = ({ children }) => (
  <Tag small variant="success">
    {children}
  </Tag>
);

const RemoveTag: React.FC<{ children: React.ReactNode | string }> = ({ children }) => (
  <Tag small variant="error">
    {children}
  </Tag>
);

const ModifyTag: React.FC<{ children: React.ReactNode | string }> = ({ children }) => (
  <Tag small variant="info">
    {children}
  </Tag>
);

const AudioEntryMetadata: React.FC<{ entry: IRepositoryCompareEntry }> = ({ entry }) => {
  if (entry.data_type !== 'audio') {
    return null;
  }

  const base = entry.base_entry?.metadata?.audio;
  const head = entry.head_entry?.metadata?.audio;
  return (
    <div>
      <Row gap={5} alignItems="center">
        {!!base && (
          <RemoveTag>
            {format.duration(base?.num_seconds)} x {base?.num_channels}ch @ {format.audioSampleRate(base?.sample_rate)}
          </RemoveTag>
        )}
        {!!base && !!head && <Icon.ArrowRight />}
        {!!head && (
          <AddTag>
            {format.duration(head?.num_seconds)} x {head?.num_channels}ch @ {format.audioSampleRate(head?.sample_rate)}
          </AddTag>
        )}
      </Row>
    </div>
  );
};

const ImageEntryMetadata: React.FC<{ entry: IRepositoryCompareEntry }> = ({ entry }) => {
  if (entry.data_type !== 'image') {
    return null;
  }

  const base = entry.base_entry?.metadata?.image;
  const head = entry.head_entry?.metadata?.image;
  return (
    <div>
      <Row gap={5} alignItems="center">
        {!!base && (
          <RemoveTag>
            {base?.width} x {base?.height} ({base?.color_space}) |{' '}
            {entry.base_entry?.size ? format.humanFileSize(entry.base_entry?.size) : '?'}
          </RemoveTag>
        )}
        {!!base && !!head && <Icon.ArrowRight />}
        {!!head && (
          <AddTag>
            {head?.width} x {head?.height} ({head?.color_space}) |{' '}
            {entry.head_entry?.size ? format.humanFileSize(entry.head_entry?.size) : '?'}
          </AddTag>
        )}
      </Row>
    </div>
  );
};

const VideoEntryMetadata: React.FC<{ entry: IRepositoryCompareEntry }> = ({ entry }) => {
  if (entry.data_type !== 'video') {
    return null;
  }

  const base = entry.base_entry?.metadata?.video;
  const head = entry.head_entry?.metadata?.video;
  return (
    <div>
      <Row gap={5} alignItems="center">
        {!!base && (
          <RemoveTag>
            {base?.width} x {base?.height}px ({Math.round(base?.num_seconds)}s)
          </RemoveTag>
        )}
        {!!base && !!head && <Icon.ArrowRight />}
        {!!head && (
          <AddTag>
            {head?.width}px x {head?.height}px ({Math.round(head?.num_seconds)}s)
          </AddTag>
        )}
      </Row>
    </div>
  );
};

const TextEntryMetadata: React.FC<{ entry: IRepositoryCompareEntry }> = ({ entry }) => {
  if (entry.data_type !== 'text') {
    return null;
  }

  const base = entry.base_entry?.metadata?.text;
  const head = entry.head_entry?.metadata?.text;
  return (
    <div>
      <Row gap={5} alignItems="center">
        {!!base && <RemoveTag>{base?.num_lines} lines</RemoveTag>}
        {!!head && <AddTag>{head?.num_lines} lines</AddTag>}
      </Row>
    </div>
  );
};

const TabularEntryMetadata: React.FC<{ entry: IRepositoryCompareEntry }> = ({ entry }) => {
  if (entry.data_type !== 'tabular') {
    return null;
  }

  const base = entry.base_entry?.metadata?.tabular;
  const head = entry.head_entry?.metadata?.tabular;
  return (
    <div>
      <Row gap={5} alignItems="center">
        {!!base && (
          <RemoveTag>
            {base?.width} x {format.humanCount(base?.height)}
          </RemoveTag>
        )}
        {!!head && (
          <AddTag>
            {head?.width} x {format.humanCount(head?.height)}
          </AddTag>
        )}
      </Row>
    </div>
  );
};

const Audio = AudioEntryMetadata;
const Image = ImageEntryMetadata;
const Video = VideoEntryMetadata;
const Text = TextEntryMetadata;
const Tabular = TabularEntryMetadata;

export { Audio, Image, Video, Text, Tabular };
